/**
 * Textos do "Excluir conta" (chamado 7116). A REGRA — quem pode excluir, a
 * situação do plano e até quando ele iria — vem pronta do backend em
 * `GET /users/me/account-deletion`; aqui só se montam as frases.
 */
import { formatBrasilia, type ApiDateValue } from '@/lib/brasiliaDate';
import type { AccountDeletionReason, AccountDeletionSituation } from '@/services/account.service';

const dataCompleta = (dia: ApiDateValue): string => formatBrasilia(dia, 'dd/MM/yyyy');

export const DELETE_ACCOUNT_TITLE = 'Excluir sua conta?';

export const DELETE_ACCOUNT_SECTION_TEXT =
  'Apaga a sua conta e todos os seus dados de forma definitiva. Se houver uma assinatura, ela é cancelada e não haverá novas cobranças.';

export const DELETE_ACCOUNT_WARNING =
  'Esta ação é definitiva. Todos os dados da conta (planejamento, lançamentos, metas e históricos) serão apagados e não poderão ser recuperados.';

const CIENTE_DOS_DADOS = 'Estou ciente de que todos os meus dados serão apagados definitivamente.';

export const deletionAcknowledgementText = (
  situation: AccountDeletionSituation,
  accessUntil: ApiDateValue | null,
): string => {
  if (!accessUntil || situation === 'free') return CIENTE_DOS_DADOS;
  if (situation === 'trial') {
    return `Estou ciente de que perderei imediatamente o restante do meu período de teste, que iria até ${dataCompleta(accessUntil)}.`;
  }
  return `Estou ciente de que tenho um plano vigente até ${dataCompleta(accessUntil)} e de que, ao excluir a conta, a assinatura será cancelada e perderei o acesso imediatamente, sem reembolso.`;
};

export const deletionMembersText = (count: number): string | null => {
  if (count <= 0) return null;
  if (count === 1) return 'O membro da sua conta também perderá o acesso e terá os dados apagados.';
  return `Os ${count} membros da sua conta também perderão o acesso e terão os dados apagados.`;
};

/** Motivos da exclusão, na ordem do select; o valor é a categoria do backend. */
export const ACCOUNT_DELETION_REASONS: ReadonlyArray<{ value: AccountDeletionReason; label: string }> = [
  { value: 'privacy', label: 'Tenho preocupações com a privacidade e a segurança dos meus dados' },
  { value: 'no_longer_use', label: 'Não vou mais usar a plataforma' },
  { value: 'switching_tool', label: 'Vou usar outra ferramenta e não quero deixar meus dados aqui' },
  { value: 'duplicate_account', label: 'Tenho outra conta e quero manter apenas uma' },
  { value: 'start_over', label: 'Quero apagar tudo e recomeçar do zero' },
  { value: 'bad_experience', label: 'Tive uma experiência ruim com a plataforma ou com o suporte' },
  { value: 'other', label: 'Outro motivo' },
];

/** Aviso da tela de login (`/login?conta=encerrada`) depois da exclusão. */
export const ACCOUNT_DELETED_NOTICE =
  'Sua conta foi excluída e os seus dados foram apagados. Para voltar a usar o Librattum, crie uma nova conta e contrate uma nova assinatura.';
